/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { isAdminUser, getUserVenues, userCanAccessVenue } from '../userUtils';
import {
  Clock, Truck, Zap, ClipboardList, Send, PackageCheck, Eye,
  ChefHat, Coffee, Check, X, Minus, Plus, AlertCircle,
} from 'lucide-react';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

const INTERNAL_VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
const HISTORY_DAYS = 7;
const HISTORY_DAYS_ADMIN = 30;
const GENERIC_USERS = [
  'Box Jordaan Bar', 'Box Jordaan Kitchen',
  'Box Plantage Bar', 'Box Plantage Kitchen',
  'Sassys', 'Supervisor - Box J', 'Supervisor - Box P',
];

const C = {
  yellow: '#F7EC71', yellowLight: '#FEFCE8', orange: '#E4AC49', orangeLight: '#FDF3DC',
  green: '#7EA594', greenLight: '#EAF2EF', cream: '#F7F6EF', charcoal: '#2F3A42',
  navy: '#445366', mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  white: '#FFFFFF', red: '#DC3545', redLight: '#FFF0F0',
  amber: '#B45309', amberLight: '#FEF3C7',
  pink: '#F2B8CB', pinkLight: '#FDF0F5', pinkText: '#C4607A',
  lavender: '#EEEDFE', lavenderText: '#3C3489',
};

const VENUE_IDENTITY = {
  'Box Jordaan':  { bg: '#EDF3FC', text: '#2563EB', border: '#BAD4F5', shortName: 'Jordaan' },
  'Box Plantage': { bg: '#C8E6D8', text: '#2D7A65', border: '#7EA594', shortName: 'Plantage' },
  'Sassys':       { bg: '#EDE0F7', text: '#6D28A0', border: '#6D28A0', shortName: 'Sassys' },
  'Prep Kitchen': { bg: C.cream,   text: C.navy,    border: C.border,  shortName: 'Prep Kitchen' },
};

// Column colours for PK combined view
const PK_COL = {
  jordaan:  { bg: '#EDF3FC', text: '#2563EB', headerBorder: '#BAD4F5' },
  plantage: { bg: C.lavender, text: C.lavenderText, headerBorder: '#C4C2F5' },
};

function getVenueIdentity(venue) {
  return VENUE_IDENTITY[venue] || { bg: C.white, text: C.navy, border: C.border, shortName: venue };
}

function buildCardTitle(venue, items = []) {
  const v = getVenueIdentity(venue);
  const depts = [...new Set(items.map(i => (i.dept || 'kitchen').toLowerCase()).filter(Boolean))];
  const hasKit = depts.length === 0 || depts.includes('kitchen');
  const hasBar = depts.includes('bar');
  if (hasKit && hasBar) return `${v.shortName} Order`;
  if (hasBar) return `${v.shortName} Bar Order`;
  return `${v.shortName} Kitchen Order`;
}

const TAB_CONFIG = {
  pending:     { label: 'Pending',   Icon: Clock,         accent: C.orange,   bg: C.orangeLight,  description: 'Orders placed, waiting to be fulfilled' },
  enroute:     { label: 'En-Route',  Icon: Truck,         accent: C.skyText,  bg: C.skyBlueLight, description: 'On their way — confirm receipt when they arrive' },
  needsaction: { label: 'To Fulfil', Icon: Zap,           accent: C.pinkText, bg: C.pinkLight,    description: 'Orders to pack and send out' },
  history:     { label: 'History',   Icon: ClipboardList, accent: C.green,    bg: C.greenLight,   description: 'Received orders — last 7 days' },
};

const STATUS_MAP = {
  pending:               { label: 'Pending',  bg: C.orangeLight,  text: C.orange  },
  'en-route':            { label: 'En-Route', bg: C.skyBlueLight, text: C.skyText },
  'partially fulfilled': { label: 'Partial',  bg: C.amberLight,   text: C.amber   },
  received:              { label: 'Received', bg: C.greenLight,   text: C.green   },
};

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || { label: status, bg: C.cream, text: C.mid };
  return <span style={{ background: s.bg, color: s.text, padding: '0.18rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function DeptIcon({ dept, size = 12 }) {
  const isBar = dept === 'bar';
  return isBar
    ? <Coffee size={size} strokeWidth={2} style={{ color: C.pinkText, flexShrink: 0 }} />
    : <ChefHat size={size} strokeWidth={2} style={{ color: C.orange, flexShrink: 0 }} />;
}

function DeptBadge({ dept }) {
  if (!dept) return null;
  const b = dept === 'bar';
  return (
    <span style={{ background: b ? C.pinkLight : C.orangeLight, color: b ? C.pinkText : C.orange, borderRadius: '4px', padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
      <DeptIcon dept={dept} size={10} />
      {b ? 'BAR' : 'KIT'}
    </span>
  );
}

function getHistoryBoundary(days) { const d = new Date(); d.setDate(d.getDate() - days); return d.toISOString().split('T')[0]; }
function fmtDate(ds) { if (!ds) return '—'; return new Date(ds + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); }
function shortName(venue) { return (venue || '').replace(/^Box /, ''); }

function LemonBurst() {
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
      {['🍋', '🌿', '🍋', '🌳', '🍋'].map((e, i) => (<span key={i} style={{ position: 'absolute', fontSize: `${1.2 + Math.random() * 1.2}rem`, top: `${20 + Math.random() * 60}%`, left: `${10 + i * 18 + Math.random() * 10}%`, animation: 'lemonFloat 1.2s ease-out forwards', animationDelay: `${i * 0.08}s`, opacity: 0 }}>{e}</span>))}
      <style>{`@keyframes lemonFloat{0%{opacity:0;transform:translateY(20px) scale(.5)}30%{opacity:1;transform:translateY(-10px) scale(1.1)}80%{opacity:1;transform:translateY(-30px) scale(1)}100%{opacity:0;transform:translateY(-60px) scale(.8)}}`}</style>
    </div>
  );
}

function SectionPill({ tabKey, isActive, onClick, count, singleTab }) {
  const tab = TAB_CONFIG[tabKey]; const Icon = tab.Icon;
  return (
    <button onClick={onClick} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', padding: '0.65rem 1.25rem', borderRadius: '14px', flex: singleTab ? '0 0 auto' : 1, minWidth: '72px', border: `2px solid ${isActive ? tab.accent : C.border}`, background: isActive ? tab.accent : C.white, color: isActive ? C.white : C.mid, cursor: 'pointer', position: 'relative', boxShadow: isActive ? `0 3px 10px ${tab.accent}44` : '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.18s' }}>
      {count > 0 && !isActive && <span style={{ position: 'absolute', top: 5, right: 5, width: 8, height: 8, borderRadius: '50%', background: tab.accent, border: `1.5px solid ${C.white}` }} />}
      <Icon size={16} strokeWidth={2} style={{ opacity: isActive ? 1 : 0.7 }} />
      <span style={{ fontSize: '0.66rem', fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap', lineHeight: 1.2 }}>{tab.label}</span>
      {count > 0 && <span style={{ background: isActive ? 'rgba(255,255,255,0.28)' : tab.accent, color: C.white, borderRadius: '999px', padding: '0.05rem 0.42rem', fontSize: '0.62rem', fontWeight: 800, minWidth: '18px', textAlign: 'center', lineHeight: 1.4 }}>{count}</span>}
    </button>
  );
}

function EmptyState({ tabKey }) {
  const tab = TAB_CONFIG[tabKey]; const Icon = tab.Icon;
  const msgs = { pending: 'No pending orders', enroute: 'Nothing en-route right now', needsaction: 'Nothing to fulfil right now', history: 'No received orders in the last 7 days' };
  return (
    <div style={{ background: tab.bg, border: `1px dashed ${tab.accent}60`, borderRadius: '12px', padding: '2.5rem 1.5rem', textAlign: 'center' }}>
      <Icon size={28} style={{ color: tab.accent, marginBottom: '0.5rem', opacity: 0.6 }} />
      <p style={{ margin: '0 0 0.3rem', fontWeight: 600, color: C.charcoal, fontSize: '0.9rem' }}>{msgs[tabKey]}</p>
      <p style={{ margin: 0, fontSize: '0.79rem', color: C.mid }}>{tab.description}</p>
    </div>
  );
}

// ── Stepper ───────────────────────────────────────────────────────────
function Stepper({ value, onChange, min = 0 }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', border: `1.5px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', height: 40, flexShrink: 0 }}>
      <button onClick={() => onChange(Math.max(min, value - 1))}
        style={{ width: 38, height: 40, border: 'none', borderRight: `1px solid ${C.border}`, background: value > min ? C.navy : C.cream, color: value > min ? C.white : C.mid, cursor: value > min ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
        <Minus size={13} />
      </button>
      <span style={{ minWidth: 38, textAlign: 'center', fontWeight: 800, fontSize: '0.95rem', color: C.charcoal, padding: '0 4px' }}>{value}</span>
      <button onClick={() => onChange(value + 1)}
        style={{ width: 38, height: 40, border: 'none', borderLeft: `1px solid ${C.border}`, background: C.navy, color: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
        <Plus size={13} />
      </button>
    </div>
  );
}

// ── localStorage helpers ──────────────────────────────────────────────
function lsKey(reqId) { return `transfer_progress_${reqId}`; }
function loadProgress(reqId) { try { return JSON.parse(localStorage.getItem(lsKey(reqId)) || 'null'); } catch { return null; } }
function saveProgress(reqId, actions, adjQtys) { try { localStorage.setItem(lsKey(reqId), JSON.stringify({ actions, adjQtys })); } catch {} }
function clearProgress(reqId) { try { localStorage.removeItem(lsKey(reqId)); } catch {} }

// ── PK Combined Fulfil View ───────────────────────────────────────────
// jordaanReqs / plantageReqs are arrays (one per dept row, max 2 each)
function PKCombinedFulfilView({ jordaanReqs, plantageReqs, onLemon, isMobile, sentBy }) {
  // Flatten all items from all rows per venue
  const jordaanItems = jordaanReqs.flatMap(r => r.items || []);
  const plantageItems = plantageReqs.flatMap(r => r.items || []);

  const allProductKeys = [];
  const seenKeys = new Set();
  [...jordaanItems, ...plantageItems].forEach(item => {
    const k = item.id || item.name;
    if (!seenKeys.has(k)) { seenKeys.add(k); allProductKeys.push(k); }
  });

  const jMap = Object.fromEntries(jordaanItems.map(i => [i.id || i.name, i]));
  const pMap = Object.fromEntries(plantageItems.map(i => [i.id || i.name, i]));

  // Per-row tick state
  const [ticked, setTicked] = useState({});
  // Adjusted qtys: { productKey: { jordaan: number|null, plantage: number|null } }
  const [adjQtys, setAdjQtys] = useState({});
  // Popup state
  const [popup, setPopup] = useState(null); // { productKey, venue: 'jordaan'|'plantage', currentQty, itemName }
  const [popupVal, setPopupVal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sendError, setSendError] = useState(null);

  const allTicked = allProductKeys.length > 0 && allProductKeys.every(k => ticked[k]);

  function getQty(productKey, venue) {
    if (adjQtys[productKey]?.[venue] !== undefined) return adjQtys[productKey][venue];
    const item = venue === 'jordaan' ? jMap[productKey] : pMap[productKey];
    return item ? item.quantity : null;
  }

  function openPopup(productKey, venue) {
    const item = venue === 'jordaan' ? jMap[productKey] : pMap[productKey];
    if (!item) return;
    const current = getQty(productKey, venue);
    setPopup({ productKey, venue, itemName: item.name, unit: item.unit });
    setPopupVal(current ?? item.quantity);
  }

  function confirmPopup() {
    if (!popup) return;
    setAdjQtys(prev => ({
      ...prev,
      [popup.productKey]: { ...(prev[popup.productKey] || {}), [popup.venue]: popupVal },
    }));
    setPopup(null);
  }

  async function handleSendBoth() {
    setSaving(true);
    setSendError(null);

    const errors = [];

    // Send all Jordaan rows (kitchen + bar may be separate rows)
    for (const req of jordaanReqs) {
      const reqItems = req.items || [];
      const updatedItems = reqItems.map(item => {
        const k = item.id || item.name;
        const qty = adjQtys[k]?.jordaan ?? item.quantity;
        return { ...item, quantityFulfilled: qty };
      });
      const allFull = updatedItems.every(i => i.quantityFulfilled >= i.quantity);
      const { error } = await supabase.from('requests').update({
        status: allFull ? 'en-route' : 'partially fulfilled',
        sent_at: new Date().toISOString(),
        sent_by: sentBy || null,
        items: updatedItems,
      }).eq('id', req.id);
    }

    // Send all Plantage rows
    for (const req of plantageReqs) {
      const reqItems = req.items || [];
      const updatedItems = reqItems.map(item => {
        const k = item.id || item.name;
        const qty = adjQtys[k]?.plantage ?? item.quantity;
        return { ...item, quantityFulfilled: qty };
      });
      const allFull = updatedItems.every(i => i.quantityFulfilled >= i.quantity);
      const { error } = await supabase.from('requests').update({
        status: allFull ? 'en-route' : 'partially fulfilled',
        sent_at: new Date().toISOString(),
        sent_by: sentBy || null,
        items: updatedItems,
      }).eq('id', req.id);
      if (error) errors.push('Plantage');
    }

    setSaving(false);

    if (errors.length > 0) {
      setSendError(`Failed to send ${errors.join(' & ')} order${errors.length > 1 ? 's' : ''}. Check connection and try again.`);
    } else {
      // Real-time subscription in App.js handles state update
      onLemon && onLemon();
      setTicked({});
      setAdjQtys({});
    }
  }

  const hasOrders = jordaanReqs.length > 0 || plantageReqs.length > 0;

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* Qty adjustment popup — top anchored */}
      {popup && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 600, padding: '0.75rem', background: 'rgba(47,58,66,0.55)' }}>
          <div style={{ background: C.white, borderRadius: '14px', padding: '1.25rem', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxWidth: '420px', margin: '0 auto' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.mid, marginBottom: '0.2rem' }}>Adjusting quantity</div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: C.charcoal }}>{popup.itemName}</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: '0.3rem', background: popup.venue === 'jordaan' ? PK_COL.jordaan.bg : PK_COL.plantage.bg, color: popup.venue === 'jordaan' ? PK_COL.jordaan.text : PK_COL.plantage.text, padding: '0.18rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 }}>
                {popup.venue === 'jordaan' ? 'Jordaan' : 'Plantage'}
              </div>
            </div>
            <div style={{ fontSize: '0.83rem', color: C.mid, marginBottom: '0.85rem' }}>What can you send?</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <Stepper value={popupVal} onChange={setPopupVal} min={0} />
              {popup.unit && <span style={{ fontSize: '0.8rem', color: C.mid, fontWeight: 600 }}>{popup.unit}</span>}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setPopup(null)}
                style={{ flex: 1, padding: '0.7rem', border: `1.5px solid ${C.border}`, borderRadius: 10, background: C.white, color: C.mid, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmPopup}
                style={{ flex: 2, padding: '0.7rem', border: 'none', borderRadius: 10, background: C.navy, color: C.white, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <GroupHeading label="To fulfil: Jordaan + Plantage" />

      {!hasOrders ? (
        <EmptyState tabKey="needsaction" />
      ) : (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
          {/* Column headers */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 48px 48px 44px' : '1fr 56px 72px 72px 44px', borderBottom: `1px solid ${C.border}`, background: C.cream }}>
            <div style={{ padding: '0.45rem 0.75rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Product</div>
            {!isMobile && <div style={{ padding: '0.45rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center' }}>Unit</div>}
            <div style={{ padding: '0.45rem 0.5rem', background: PK_COL.jordaan.bg, fontSize: '0.65rem', fontWeight: 700, color: PK_COL.jordaan.text, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderLeft: `1px solid ${PK_COL.jordaan.headerBorder}` }}>J'dan</div>
            <div style={{ padding: '0.45rem 0.5rem', background: PK_COL.plantage.bg, fontSize: '0.65rem', fontWeight: 700, color: PK_COL.plantage.text, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderLeft: `1px solid ${PK_COL.plantage.headerBorder}` }}>Plantage</div>
            <div style={{ padding: '0.45rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center' }}></div>
          </div>

          {/* Product rows */}
          {allProductKeys.map(k => {
            const jItem = jMap[k];
            const pItem = pMap[k];
            const item = jItem || pItem;
            const isTicked = !!ticked[k];
            const jQty = getQty(k, 'jordaan');
            const pQty = getQty(k, 'plantage');
            const jAdj = adjQtys[k]?.jordaan !== undefined;
            const pAdj = adjQtys[k]?.plantage !== undefined;
            const rowBg = isTicked ? C.greenLight : C.white;
            const textColor = isTicked ? '#2D7A65' : C.charcoal;

            return (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 48px 48px 44px' : '1fr 56px 72px 72px 44px', borderBottom: `1px solid ${C.border}`, background: rowBg, transition: 'background 0.18s' }}>
                {/* Product name + unit on mobile */}
                <div style={{ padding: '0.65rem 0.75rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.88rem', color: textColor, lineHeight: 1.3 }}>{item.name}</span>
                  {isMobile && item.unit && (
                    <span style={{ fontSize: '0.65rem', color: isTicked ? '#2D7A65' : C.mid, marginTop: '0.1rem' }}>{item.unit}</span>
                  )}
                </div>

                {/* Unit col — desktop only */}
                {!isMobile && (
                  <div style={{ padding: '0.65rem 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ background: C.skyBlueLight, color: C.skyText, borderRadius: 4, padding: '0.05rem 0.35rem', fontSize: '0.65rem', fontWeight: 700 }}>{item.unit || '—'}</span>
                  </div>
                )}

                {/* Jordaan qty */}
                <div style={{ padding: '0.65rem 0.5rem', background: isTicked ? 'transparent' : PK_COL.jordaan.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: `1px solid ${isTicked ? C.border : PK_COL.jordaan.headerBorder}`, transition: 'background 0.18s' }}>
                  {jQty !== null ? (
                    <button onClick={() => !isTicked && openPopup(k, 'jordaan')}
                      style={{ fontWeight: 700, fontSize: '0.95rem', color: isTicked ? '#2D7A65' : jAdj ? C.amber : PK_COL.jordaan.text, background: 'none', border: 'none', cursor: isTicked ? 'default' : 'pointer', padding: 0, textDecoration: !isTicked && !jAdj ? 'underline dotted' : 'none', textUnderlineOffset: '2px' }}>
                      {jQty}
                    </button>
                  ) : (
                    <span style={{ color: C.mid, fontSize: '0.85rem' }}>—</span>
                  )}
                </div>

                {/* Plantage qty */}
                <div style={{ padding: '0.65rem 0.5rem', background: isTicked ? 'transparent' : PK_COL.plantage.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: `1px solid ${isTicked ? C.border : PK_COL.plantage.headerBorder}`, transition: 'background 0.18s' }}>
                  {pQty !== null ? (
                    <button onClick={() => !isTicked && openPopup(k, 'plantage')}
                      style={{ fontWeight: 700, fontSize: '0.95rem', color: isTicked ? '#2D7A65' : pAdj ? C.amber : PK_COL.plantage.text, background: 'none', border: 'none', cursor: isTicked ? 'default' : 'pointer', padding: 0, textDecoration: !isTicked && !pAdj ? 'underline dotted' : 'none', textUnderlineOffset: '2px' }}>
                      {pQty}
                    </button>
                  ) : (
                    <span style={{ color: C.mid, fontSize: '0.85rem' }}>—</span>
                  )}
                </div>

                {/* Tick / Undo */}
                <div style={{ padding: '0.45rem 0.35rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isTicked ? (
                    <button onClick={() => setTicked(p => ({ ...p, [k]: false }))}
                      style={{ width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, color: C.mid, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', WebkitTapHighlightColor: 'transparent' }}>
                      ↩
                    </button>
                  ) : (
                    <button onClick={() => setTicked(p => ({ ...p, [k]: true }))}
                      style={{ width: 34, height: 34, borderRadius: 8, border: `2px solid ${C.green}`, background: C.white, color: C.green, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' }}>
                      <Check size={17} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Send Both footer */}
          <div style={{ padding: '0.85rem 1rem', borderTop: `1px solid ${C.border}`, background: C.cream }}>
            {sendError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.redLight, border: `1px solid ${C.red}20`, borderRadius: 8, padding: '0.6rem 0.85rem', marginBottom: '0.65rem' }}>
                <AlertCircle size={14} style={{ color: C.red, flexShrink: 0 }} />
                <span style={{ fontSize: '0.8rem', color: C.red, fontWeight: 600 }}>{sendError}</span>
              </div>
            )}
            {!allTicked && (
              <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: C.mid, textAlign: 'center' }}>Tick every item to send</p>
            )}
            <button onClick={handleSendBoth} disabled={!allTicked || saving}
              style={{ width: '100%', padding: '0.85rem', border: 'none', borderRadius: 10, background: allTicked && !saving ? C.green : C.border, color: allTicked && !saving ? C.white : C.mid, fontWeight: 800, fontSize: '0.95rem', cursor: allTicked && !saving ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.18s' }}>
              <Send size={15} />
              {saving ? 'Sending…' : 'Send to Both Venues'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transfer Modal ────────────────────────────────────────────────────
function TransferModal({ req, mode, userDept, isAdmin, isManager, isPK, onSend, onReceive, onAmendPending, onClose, onLemon, isMobile, onNavigate }) {
  const visibleItems = req.items || [];

  const defaultAdjQtys = () => {
    const init = {};
    visibleItems.forEach(item => {
      const k = item.id || item.name;
      init[k] = mode === 'receive' ? (item.quantityFulfilled ?? item.quantity) : item.quantity;
    });
    return init;
  };

  const saved = (mode === 'send' || mode === 'receive') ? loadProgress(req.id) : null;
  const [actions, setActions] = useState(saved?.actions || {});
  const [adjQtys, setAdjQtys] = useState(saved?.adjQtys || defaultAdjQtys());

  useEffect(() => {
    if (mode === 'send' || mode === 'receive') {
      saveProgress(req.id, actions, adjQtys);
    }
  }, [actions, adjQtys]);

  useEffect(() => {
    if (!isMobile) return;
    window.history.pushState({ modalOpen: true }, '');
    const handler = (e) => { onClose(); };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [isMobile]);

  const [pendingQtys, setPendingQtys] = useState(() => {
    const init = {};
    visibleItems.forEach(item => { init[item.id || item.name] = item.quantity; });
    return init;
  });
  const [editingKey, setEditingKey] = useState(null);
  const [editVal, setEditVal]       = useState('');
  const [note, setNote]             = useState(req.notes || '');
  const [initials, setInitials]     = useState('');
  const [saving, setSaving]         = useState(false);
  const [savingKey, setSavingKey]   = useState(null);
  const [sendError, setSendError]   = useState(null);

  const orderFrom  = req.orderFrom || req.order_from || '';
  const placedBy   = req.placedBy || req.placed_by || '';
  const amendedBy  = req.amended_by || req.amendedBy || '';
  const deptStatus = req.dept_status || {};
  const myDeptDone = !!(userDept && deptStatus[userDept] === 'received');

  const cardTitle = buildCardTitle(req.venue, req.items);
  const headerBg =
    mode === 'send'    ? C.green :
    mode === 'receive' ? C.skyText :
    req.status === 'pending'              ? C.orange :
    req.status === 'en-route' || req.status === 'partially fulfilled' ? C.skyText :
    req.status === 'received'             ? C.green :
    C.navy;

  const allActioned = visibleItems.length > 0 && visibleItems.every(i => actions[i.id || i.name]);
  const canReceive  = allActioned && initials.trim().length > 0 && !myDeptDone;

  const kitItems = visibleItems.filter(i => (i.dept || 'kitchen') !== 'bar');
  const barItems = visibleItems.filter(i => (i.dept || '') === 'bar');

  function getFinalQty(item) {
    const k = item.id || item.name;
    const action = actions[k];
    const base = mode === 'receive' ? (item.quantityFulfilled ?? item.quantity) : item.quantity;
    if (!action || action === 'tick') return base;
    return adjQtys[k] ?? base;
  }

  async function doSend() {
    setSaving(true);
    setSendError(null);
    const qtys = {};
    visibleItems.forEach(item => { qtys[item.id || item.name] = getFinalQty(item); });
    const { ok, error } = await onSend(req, qtys, note);
    if (!ok) {
      setSendError('Failed to send — check your connection and try again.');
      setSaving(false);
      return;
    }
    clearProgress(req.id);
    setSaving(false);
    onLemon && onLemon();
    onClose();
  }

  async function doReceive() {
    if (!initials.trim()) return;
    setSaving(true);
    setSendError(null);
    const qtys = {};
    visibleItems.forEach(item => { qtys[item.id || item.name] = getFinalQty(item); });
    const { ok } = await onReceive(req, qtys, note, userDept, isAdmin || isManager, initials.trim().toUpperCase());
    if (!ok) {
      setSendError('Failed to confirm receipt — check your connection and try again.');
      setSaving(false);
      return;
    }
    clearProgress(req.id);
    setSaving(false);
    onLemon && onLemon();
    onClose();
  }

  async function savePendingQty(item, newQty) {
    const k = item.id || item.name;
    setSavingKey(k);
    const updatedItems = (req.items || []).map(i => (i.id || i.name) === k ? { ...i, quantity: newQty } : i);
    await onAmendPending(req, updatedItems);
    setSavingKey(null);
  }

  const wrapStyle = isMobile
    ? { position: 'fixed', inset: 0, zIndex: 500, background: C.white, display: 'flex', flexDirection: 'column', overflowY: 'auto', fontFamily: 'system-ui, sans-serif' }
    : { position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(44,58,66,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'system-ui, sans-serif' };
  const innerStyle = isMobile
    ? { flex: 1, display: 'flex', flexDirection: 'column' }
    : { background: C.white, borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', display: 'flex', flexDirection: 'column' };

  function renderDeptHeader(dept) {
    const isBar = dept === 'bar';
    return (
      <div style={{ padding: '0.32rem 1rem', background: isBar ? C.pinkLight : C.orangeLight, display: 'flex', alignItems: 'center', gap: 5, borderBottom: `1px solid ${C.border}` }}>
        <DeptIcon dept={dept} size={11} />
        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: isBar ? C.pinkText : C.orange, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{isBar ? 'Bar' : 'Kitchen'}</span>
      </div>
    );
  }

  function renderActionRow(item) {
    const k        = item.id || item.name;
    const action   = actions[k];
    const base     = mode === 'receive' ? (item.quantityFulfilled ?? item.quantity) : item.quantity;
    const adj      = adjQtys[k] ?? base;
    const isTick   = action === 'tick';
    const isCross  = action === 'cross';

    const orderedQty    = item.quantity;
    const fulfilledQty  = item.quantityFulfilled;
    const hasVariance   = mode === 'receive' && fulfilledQty !== undefined && fulfilledQty !== orderedQty;
    const isZeroSent    = mode === 'receive' && fulfilledQty === 0;

    let rowBg = C.white;
    if (isZeroSent && !action)   rowBg = C.redLight;
    else if (hasVariance && !action) rowBg = C.amberLight;
    if (isTick)  rowBg = C.greenLight;
    if (isCross) rowBg = C.redLight;

    return (
      <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '0.8rem 1rem', borderBottom: `1px solid ${C.border}`, background: rowBg, transition: 'background 0.18s', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.92rem', color: C.charcoal, lineHeight: 1.3 }}>{item.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            <span style={{ background: C.skyBlueLight, color: C.skyText, borderRadius: 4, padding: '0.05rem 0.35rem', fontSize: '0.68rem', fontWeight: 700 }}>{item.unit}</span>
            {hasVariance && !action && (
              <span style={{ fontSize: '0.72rem', color: isZeroSent ? C.red : C.amber, fontWeight: 600 }}>
                sent {fulfilledQty}, ordered {orderedQty}
              </span>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', minWidth: 48 }}>
          {isCross ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
              <span style={{ fontSize: '0.75rem', color: C.mid, textDecoration: 'line-through' }}>{base}</span>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: C.charcoal }}>{adj}</span>
            </div>
          ) : (
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: isTick ? C.green : C.charcoal }}>{base}</span>
          )}
        </div>

        {isCross && <Stepper value={adj} onChange={v => setAdjQtys(p => ({ ...p, [k]: v }))} min={0} />}

        {!isTick && !isCross && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button onClick={() => setActions(p => ({ ...p, [k]: 'cross' }))}
              style={{ width: 42, height: 42, borderRadius: 10, border: `2px solid ${C.red}`, background: C.white, color: C.red, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' }}>
              <X size={20} strokeWidth={2.5} />
            </button>
            <button onClick={() => setActions(p => ({ ...p, [k]: 'tick' }))}
              style={{ width: 42, height: 42, borderRadius: 10, border: `2px solid ${C.green}`, background: C.white, color: C.green, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' }}>
              <Check size={20} strokeWidth={2.5} />
            </button>
          </div>
        )}

        {(isTick || isCross) && (
          <button onClick={() => { setActions(p => ({ ...p, [k]: null })); setAdjQtys(p => ({ ...p, [k]: base })); }}
            style={{ width: 42, height: 42, borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.white, color: C.mid, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', WebkitTapHighlightColor: 'transparent' }}>
            ↩
          </button>
        )}
      </div>
    );
  }

  function renderPendingRow(item) {
    const k = item.id || item.name;
    const isReceived = req.status === 'received';
    const displayQty = isReceived
      ? (item.quantityReceived ?? item.quantityFulfilled ?? item.quantity)
      : item.quantity;
    const orderedQty   = item.quantity;
    const fulfilledQty = item.quantityFulfilled;
    const hasVariance  = fulfilledQty !== undefined && fulfilledQty !== orderedQty;
    const isZero       = fulfilledQty === 0;
    let rowBg = C.white;
    if (isZero)      rowBg = C.redLight;
    else if (hasVariance) rowBg = C.amberLight;

    return (
      <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '0.8rem 1rem', borderBottom: `1px solid ${C.border}`, background: rowBg, gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.92rem', color: C.charcoal, lineHeight: 1.3 }}>{item.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            <span style={{ background: C.skyBlueLight, color: C.skyText, borderRadius: 4, padding: '0.05rem 0.35rem', fontSize: '0.68rem', fontWeight: 700 }}>{item.unit}</span>
            {hasVariance && (
              <span style={{ fontSize: '0.72rem', color: isZero ? C.red : C.amber, fontWeight: 600 }}>
                sent {fulfilledQty}, ordered {orderedQty}
              </span>
            )}
          </div>
        </div>
        <span style={{ fontWeight: 700, fontSize: '1rem', color: C.charcoal, minWidth: 32, textAlign: 'center' }}>{displayQty}</span>
      </div>
    );
  }

  const doneCount = Object.values(actions).filter(Boolean).length;
  const totalCount = visibleItems.length;

  return (
    <div style={wrapStyle} onClick={!isMobile ? onClose : undefined}>
      <div style={innerStyle} onClick={e => e.stopPropagation()}>

        <div style={{ background: headerBg, padding: isMobile ? '14px 16px 12px 64px' : '18px 24px 14px', borderRadius: isMobile ? 0 : '16px 16px 0 0', position: 'sticky', top: 0, zIndex: 10, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 800, fontSize: 17, color: C.white }}>{cardTitle}</span>
                <StatusPill status={req.status} />
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.72)', marginTop: 3 }}>
                {mode === 'send' && <>To <strong style={{ color: C.white }}>{shortName(req.venue)}</strong></>}
                {mode === 'receive' && <>From <strong style={{ color: C.white }}>{shortName(orderFrom)}</strong></>}
                {mode === 'view' && orderFrom && <>From <strong style={{ color: C.white }}>{shortName(orderFrom)}</strong></>}
                {placedBy && <> · placed by <strong style={{ color: C.white }}>{placedBy}</strong></>}
                {amendedBy && <> · amended by <strong style={{ color: C.white }}>{amendedBy}</strong></>}
              </div>
            </div>
            <button onClick={onClose}
              style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 8, cursor: 'pointer', color: C.white, padding: 7, display: 'flex', flexShrink: 0, WebkitTapHighlightColor: 'transparent' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0, background: C.cream, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {[['Ordered', fmtDate(req.date)], ['Delivery', fmtDate(req.delivery_date)], ['Sent', (req.sent_at || req.sentAt) ? fmtDate((req.sent_at || req.sentAt).split('T')[0]) : '—'], ['Received', (req.received_at || req.receivedAt) ? fmtDate((req.received_at || req.receivedAt).split('T')[0]) : '—']].map(([l, v]) => (
            <div key={l} style={{ textAlign: 'center', padding: '10px 4px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{l}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.charcoal }}>{v}</div>
            </div>
          ))}
        </div>

        {mode === 'receive' && myDeptDone && (
          <div style={{ background: C.greenLight, padding: '10px 16px', fontSize: 13, color: C.green, fontWeight: 600, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
            ✓ You have already confirmed receipt of {userDept} items.
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', padding: '0.38rem 1rem', background: C.cream, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ flex: 1, fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Product</div>
          <div style={{ minWidth: 48, textAlign: 'center', fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Qty</div>
          {(mode === 'send' || mode === 'receive') && (
            <div style={{ minWidth: 104, textAlign: 'center', fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Fulfil</div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          {kitItems.length > 0 && <>{renderDeptHeader('kitchen')}{kitItems.map(mode === 'view' ? renderPendingRow : renderActionRow)}</>}
          {barItems.length > 0 && <>{renderDeptHeader('bar')}{barItems.map(mode === 'view' ? renderPendingRow : renderActionRow)}</>}
        </div>

        <div style={{ padding: '14px 16px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.mid, marginBottom: 6 }}>Notes — optional</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder='Add a note for the team…' rows={2}
            style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${note.trim() ? C.navy : C.border}`, borderRadius: 8, fontSize: 13, color: C.charcoal, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: C.white, transition: 'border-color 0.15s' }} />
        </div>

        <div style={{ padding: '0 16px 28px', flexShrink: 0 }}>
          {sendError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.redLight, border: `1px solid ${C.red}20`, borderRadius: 8, padding: '0.6rem 0.85rem', marginBottom: '0.65rem' }}>
              <AlertCircle size={14} style={{ color: C.red, flexShrink: 0 }} />
              <span style={{ fontSize: '0.8rem', color: C.red, fontWeight: 600 }}>{sendError}</span>
            </div>
          )}

          {mode === 'send' && (
            allActioned
              ? <button onClick={doSend} disabled={saving}
                  style={{ width: '100%', padding: 14, background: C.green, color: C.white, border: 'none', borderRadius: 12, cursor: 'pointer', fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Send size={16} />{saving ? 'Sending…' : 'Send to Venue'}
                </button>
              : <p style={{ margin: 0, fontSize: 13, color: C.mid, textAlign: 'center', padding: '6px 0' }}>Tick or cross every item to continue</p>
          )}

          {mode === 'receive' && !myDeptDone && (
            allActioned ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.navy, marginBottom: 6 }}>Your initials — required</label>
                  <input value={initials} onChange={e => setInitials(e.target.value.toUpperCase())} placeholder='e.g. EG' maxLength={4}
                    style={{ width: 90, padding: '8px 12px', textAlign: 'center', border: `2px solid ${initials.trim() ? C.navy : C.border}`, borderRadius: 8, fontSize: '1.2rem', fontWeight: 800, color: C.navy, outline: 'none', letterSpacing: '0.12em', boxSizing: 'border-box' }} />
                </div>
                <button onClick={doReceive} disabled={!canReceive || saving}
                  style={{ width: '100%', padding: 14, background: canReceive ? C.green : C.border, color: canReceive ? C.white : C.mid, border: 'none', borderRadius: 12, cursor: canReceive ? 'pointer' : 'default', fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <PackageCheck size={16} />{saving ? 'Saving…' : 'Confirm Receipt'}
                </button>
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: C.mid, textAlign: 'center', padding: '6px 0' }}>Tick or cross every item to continue</p>
            )
          )}

          {mode === 'view' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {req.status === 'pending' && onNavigate && (
                <button onClick={() => onNavigate('orders')}
                  style={{ width: '100%', padding: 14, background: C.navy, color: C.white, border: 'none', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  Edit Order
                </button>
              )}
              <button onClick={onClose}
                style={{ width: '100%', padding: 14, background: req.status === 'pending' ? C.white : C.navy, color: req.status === 'pending' ? C.mid : C.white, border: req.status === 'pending' ? `1.5px solid ${C.border}` : 'none', borderRadius: 12, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
                {req.status === 'pending' ? 'Close' : 'Done'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Transfer card ─────────────────────────────────────────────────────
function TransferCard({ req, mode, userDept, isAdmin, isManager, isPK, onSend, onReceive, onAmendPending, onLemon, isMobile, onNavigate }) {
  const [showModal, setShowModal] = useState(false);
  const orderFrom = req.orderFrom || req.order_from || '';
  const placedBy  = req.placedBy || req.placed_by || '';
  const sentBy    = req.sent_by || '';
  const v         = getVenueIdentity(req.venue);
  const cardTitle = buildCardTitle(req.venue, req.items);
  const items     = req.items || [];
  const kitCnt    = items.filter(i => (i.dept || 'kitchen') !== 'bar').length;
  const barCnt    = items.filter(i => (i.dept || '') === 'bar').length;
  const ds        = req.dept_status || {};

  const actionBg     = mode === 'send' ? C.green : mode === 'receive' ? C.skyText : mode === 'sent' ? C.cream : C.white;
  const actionColor  = mode === 'send' || mode === 'receive' ? C.white : C.charcoal;
  const actionBorder = mode === 'view' || mode === 'sent' ? `1.5px solid ${C.border}` : 'none';
  const actionLabel  = mode === 'send'
    ? <><Send size={12} style={{ marginRight: 4 }} />Fulfil</>
    : mode === 'receive'
    ? <><PackageCheck size={12} style={{ marginRight: 4 }} />Receive</>
    : mode === 'sent'
    ? <><Truck size={12} style={{ marginRight: 4 }} />Sent</>
    : <><Eye size={12} style={{ marginRight: 4 }} />View</>;

  return (
    <>
      {showModal && (
        <TransferModal
          req={req} mode={mode} userDept={userDept} isAdmin={isAdmin} isManager={isManager} isPK={isPK}
          onSend={onSend} onReceive={onReceive} onAmendPending={onAmendPending}
          onClose={() => setShowModal(false)} onLemon={onLemon} isMobile={isMobile} onNavigate={onNavigate}
        />
      )}

      <div onClick={() => setShowModal(true)}
        style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', marginBottom: '0.5rem', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'box-shadow 0.15s', WebkitTapHighlightColor: 'transparent' }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.08)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>

        <div style={{ display: 'flex', flex: 1 }}>
          <div style={{ width: 5, flexShrink: 0, background: v.bg, borderRight: `1px solid ${v.border}` }} />
          <div style={{ flex: 1, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.28rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.92rem', fontWeight: 700, color: C.charcoal }}>{cardTitle}</span>
                <StatusPill status={req.status} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {kitCnt > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', color: C.orange, fontWeight: 600 }}><ChefHat size={11} strokeWidth={2} /> {kitCnt}</span>}
                {barCnt > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', color: C.pinkText, fontWeight: 600 }}><Coffee size={11} strokeWidth={2} /> {barCnt}</span>}
                {(kitCnt > 0 || barCnt > 0) && <span style={{ fontSize: '0.72rem', color: C.mid }}>·</span>}
                {orderFrom && <span style={{ fontSize: '0.72rem', color: C.mid }}>from {shortName(orderFrom)}</span>}
                {placedBy && <><span style={{ fontSize: '0.72rem', color: C.mid }}>·</span><span style={{ fontSize: '0.72rem', color: C.mid }}>placed by <strong style={{ color: C.charcoal }}>{placedBy}</strong></span></>}
                {sentBy && <><span style={{ fontSize: '0.72rem', color: C.mid }}>·</span><span style={{ fontSize: '0.72rem', color: C.mid }}>sent by <strong style={{ color: C.charcoal }}>{sentBy}</strong></span></>}
                {mode === 'sent' && req.sent_at && <><span style={{ fontSize: '0.72rem', color: C.mid }}>·</span><span style={{ fontSize: '0.72rem', color: C.skyText, fontWeight: 600 }}>at {new Date(req.sent_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span></>}
                {req.delivery_date && <><span style={{ fontSize: '0.72rem', color: C.mid }}>·</span><span style={{ fontSize: '0.72rem', color: C.skyText, fontWeight: 600 }}>{fmtDate(req.delivery_date)}</span></>}
              </div>
              {Object.keys(ds).length > 0 && (
                <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                  {Object.entries(ds).map(([dept, status]) => {
                    const done = status === 'received'; const who = (req.received_by || {})[dept];
                    return (
                      <span key={dept} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: done ? C.greenLight : C.amberLight, color: done ? C.green : C.amber, padding: '0.1rem 0.45rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700 }}>
                        <DeptIcon dept={dept} size={9} />{dept} {done ? `✓${who ? ` ${who}` : ''}` : '…'}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <button onClick={e => { e.stopPropagation(); setShowModal(true); }}
              style={{ padding: '0.35rem 0.85rem', border: actionBorder, borderRadius: 8, background: actionBg, color: actionColor, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.15s', display: 'flex', alignItems: 'center', flexShrink: 0, gap: 3 }}>
              {actionLabel}
            </button>
          </div>
        </div>
        <div style={{ height: 4, background: v.bg, borderTop: `1px solid ${v.border}` }} />
      </div>
    </>
  );
}

function GroupHeading({ label }) {
  return (
    <div style={{ fontSize: '0.73rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem', marginTop: '0.15rem', paddingBottom: '0.3rem', borderBottom: `1px solid ${C.border}` }}>
      {label}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────
export default function Transfers({ requests, setRequests, currentUser, onNavigate }) {
  const isAdmin   = isAdminUser(currentUser);
  const isPK      = currentUser?.venue === 'Prep Kitchen';
  const userVenue = currentUser?.venue || '';
  const userVenues = getUserVenues(currentUser);
  const DEPT_ROLES = ['kitchen', 'bar'];
  const userDept  = DEPT_ROLES.includes(currentUser?.role) ? currentUser.role : null;
  const isManager = currentUser?.role === 'manager' || currentUser?.role === 'supervisor';
  const isMobile  = useIsMobile();
  const isGenericUser = GENERIC_USERS.includes(currentUser?.name);

  const defaultTab = isPK ? 'needsaction' : 'pending';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showLemon, setShowLemon] = useState(false);
  const [showSentByModal, setShowSentByModal] = useState(false);
  const [pendingSentByReq, setPendingSentByReq] = useState(null);
  const [pendingSentByQtys, setPendingSentByQtys] = useState(null);
  const [pendingSentByNote, setPendingSentByNote] = useState('');
  const [sentByInitials, setSentByInitials] = useState('');
  function lemon() { setShowLemon(true); setTimeout(() => setShowLemon(false), 1400); }

  const historyDays = (isAdmin || isManager) ? HISTORY_DAYS_ADMIN : HISTORY_DAYS;

  const allInternal = requests.filter(r => {
    if (r.source === 'stocktake_external') return false;
    return INTERNAL_VENUES.includes(r.orderFrom || r.order_from);
  });
  const historyBoundary = getHistoryBoundary(historyDays);

  const pendingOrders = allInternal.filter(r => {
    if (r.status !== 'pending') return false;
    if (isAdmin) return true;
    return userCanAccessVenue(currentUser, r.venue);
  });

  // En-route: for PK show orders they fulfilled — view only
  const enRouteOrders = allInternal.filter(r => {
    if (r.status !== 'en-route' && r.status !== 'partially fulfilled') return false;
    if (isAdmin) return true;
    if (isPK) return (r.orderFrom || r.order_from) === 'Prep Kitchen';
    return userCanAccessVenue(currentUser, r.venue);
  });

  // needsAction: pending AND en-route/partial (fulfiller keeps visibility after sending)
  const needsActionOrders = allInternal.filter(r => {
    if (r.status !== 'pending' && r.status !== 'en-route' && r.status !== 'partially fulfilled') return false;
    const of = r.orderFrom || r.order_from;
    if (isAdmin) return true;
    if (isPK) return of === 'Prep Kitchen';
    return userVenues.includes(of) && of !== 'Prep Kitchen';
  });

  const historyOrders = allInternal.filter(r => {
    if (r.status !== 'received') return false;
    if (r.date < historyBoundary) return false;
    if (isAdmin) return true;
    if (isPK) return (r.orderFrom || r.order_from) === 'Prep Kitchen';
    return userCanAccessVenue(currentUser, r.venue) || userVenues.includes(r.orderFrom || r.order_from);
  });

  const counts = { pending: pendingOrders.length, enroute: enRouteOrders.length, needsaction: needsActionOrders.length, history: historyOrders.length };
  // PK sees: To Fulfil + En-Route (to verify what they've sent)
  const mainTabs = isPK ? ['needsaction', 'enroute'] : ['pending', 'needsaction', 'enroute'];
  const [showHistory, setShowHistory] = useState(false);

  function getMode(req, tab) {
    if (tab === 'history' || tab === 'pending') return 'view';
    const of = req.orderFrom || req.order_from;
    if (tab === 'enroute') {
      if (isPK) return 'view';
      const isReceiver = isAdmin || isManager || userCanAccessVenue(currentUser, req.venue);
      return isReceiver ? 'receive' : 'view';
    }
    if (tab === 'needsaction') {
      if (req.status === 'en-route' || req.status === 'partially fulfilled') return 'sent';
      const isFulfiller = isAdmin || (isPK && of === 'Prep Kitchen') || (!isPK && userVenues.includes(of));
      return isFulfiller ? 'send' : 'view';
    }
    return 'view';
  }

  // ── handleSend: write to DB only, let real-time update state ─────────
  async function handleSend(req, qtys, notes, sentBy) {
    const updatedItems = (req.items || []).map(item => {
      const k = item.id || item.name;
      const qty = parseFloat(qtys[k]);
      return { ...item, quantityFulfilled: isNaN(qty) ? item.quantity : qty };
    });
    const allFull = updatedItems.every(i => i.quantityFulfilled >= i.quantity);
    const newStatus = allFull ? 'en-route' : 'partially fulfilled';
    const sentAt = new Date().toISOString();
    const { error } = await supabase.from('requests').update({
      status: newStatus,
      sent_at: sentAt,
      sent_by: sentBy || null,
      items: updatedItems,
      notes: notes || null,
    }).eq('id', req.id);
    return { ok: !error, error };
  }

  // Wrapper: prompt generic users for initials before sending
  function handleSendWithAudit(req, qtys, notes) {
    if (isGenericUser) {
      setPendingSentByReq(req);
      setPendingSentByQtys(qtys);
      setPendingSentByNote(notes);
      setSentByInitials('');
      setShowSentByModal(true);
      return Promise.resolve({ ok: true }); // modal takes over
    }
    return handleSend(req, qtys, notes, currentUser?.name || '');
  }

  async function confirmSentByInitials() {
    if (!sentByInitials.trim() || !pendingSentByReq) return;
    setShowSentByModal(false);
    await handleSend(pendingSentByReq, pendingSentByQtys, pendingSentByNote, sentByInitials.trim().toUpperCase());
    setPendingSentByReq(null);
    setPendingSentByQtys(null);
    setPendingSentByNote('');
    lemon();
  }

  // ── handleReceive: write to DB only, let real-time update state ──────
  async function handleReceive(req, qtys, notes, receivingDept, fullAccess, initials) {
    const updatedItems = (req.items || []).map(item => {
      if (!fullAccess && receivingDept && (item.dept || 'kitchen').toLowerCase() !== receivingDept.toLowerCase()) return item;
      const k = item.id || item.name; const qty = parseFloat(qtys[k]);
      return { ...item, quantityReceived: isNaN(qty) ? (item.quantityFulfilled ?? item.quantity) : qty };
    });
    const existingDeptStatus = req.dept_status || {};
    let newDeptStatus = { ...existingDeptStatus };
    if (fullAccess) { const depts = [...new Set(updatedItems.map(i => (i.dept || 'kitchen').toLowerCase()))]; depts.forEach(d => { newDeptStatus[d] = 'received'; }); }
    else if (receivingDept) { newDeptStatus[receivingDept] = 'received'; }
    const existingReceivedBy = req.received_by || {};
    let newReceivedBy = { ...existingReceivedBy };
    if (initials) {
      if (fullAccess) { const depts = [...new Set(updatedItems.map(i => (i.dept || 'kitchen').toLowerCase()))]; depts.forEach(d => { newReceivedBy[d] = initials; }); }
      else if (receivingDept) { newReceivedBy[receivingDept] = initials; }
    }
    const itemDepts = [...new Set((req.items || []).map(i => (i.dept || 'kitchen').toLowerCase()))];
    const allDeptsReceived = itemDepts.every(d => newDeptStatus[d] === 'received');
    const newStatus = allDeptsReceived ? 'received' : 'en-route';
    const receivedAt = allDeptsReceived ? new Date().toISOString() : null;
    const updatePayload = {
      dept_status: newDeptStatus,
      received_by: newReceivedBy,
      items: updatedItems,
      notes: notes || req.notes || null,
      ...(allDeptsReceived ? { status: 'received', received_at: receivedAt } : {}),
    };
    const { error } = await supabase.from('requests').update(updatePayload).eq('id', req.id);
    // Real-time subscription in App.js handles state update — do NOT call setRequests
    return { ok: !error, error };
  }

  async function handleAmendPending(req, updatedItems) {
    const amendedBy = currentUser?.name || currentUser?.initials || currentUser?.email || '';
    const { error } = await supabase.from('requests').update({ items: updatedItems, amended_by: amendedBy || null }).eq('id', req.id);
    // Real-time will update state; optimistically patch locally for immediate feedback
    if (!error) setRequests(prev => prev.map(r => r.id === req.id ? { ...r, items: updatedItems, amended_by: amendedBy } : r));
  }

  function groupBy(orders, keyFn) { const g = {}; orders.forEach(r => { const k = keyFn(r); if (!g[k]) g[k] = []; g[k].push(r); }); return g; }

  function renderCards(orders, tab) {
    return orders.map(r => <TransferCard key={r.id} req={r} mode={getMode(r, tab)} userDept={userDept} isAdmin={isAdmin} isManager={isManager} isPK={isPK} onSend={handleSendWithAudit} onReceive={handleReceive} onAmendPending={handleAmendPending} onLemon={lemon} isMobile={isMobile} onNavigate={onNavigate} />);
  }

  function renderIncoming(orders, tab) {
    if (!orders.length) return <EmptyState tabKey={tab} />;
    return Object.entries(groupBy(orders, r => r.orderFrom || r.order_from || 'Unknown')).map(([src, reqs]) => (
      <div key={src} style={{ marginBottom: '1.25rem' }}>
        <GroupHeading label={`Incoming from: ${shortName(src)}`} />
        {renderCards(reqs, tab)}
      </div>
    ));
  }

  // ── PK Needs Action: merged Jordaan+Plantage table + Sassys card ─────
  function renderPKNeedsAction() {
    // Collect ALL rows for Jordaan and Plantage (may be bar + kitchen separately)
    const jordaanReqs  = needsActionOrders.filter(r => r.venue === 'Box Jordaan');
    const plantageReqs = needsActionOrders.filter(r => r.venue === 'Box Plantage');
    const sassysOrders = needsActionOrders.filter(r => r.venue === 'Sassys');
    const otherOrders  = needsActionOrders.filter(r => r.venue !== 'Box Jordaan' && r.venue !== 'Box Plantage' && r.venue !== 'Sassys');

    const hasJordaanOrPlantage = jordaanReqs.length > 0 || plantageReqs.length > 0;

    return (
      <div>
        {/* Merged Jordaan + Plantage table */}
        {hasJordaanOrPlantage && (
          <PKCombinedFulfilView
            jordaanReqs={jordaanReqs}
            plantageReqs={plantageReqs}
            onLemon={lemon}
            isMobile={isMobile}
            sentBy={isGenericUser ? null : (currentUser?.name || '')}
          />
        )}

        {/* Sassys — separate card */}
        {sassysOrders.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <GroupHeading label="To fulfil: Sassys" />
            {renderCards(sassysOrders, 'needsaction')}
          </div>
        )}

        {/* Other venues (edge case) */}
        {otherOrders.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <GroupHeading label="Other orders" />
            {renderCards(otherOrders, 'needsaction')}
          </div>
        )}

        {/* Empty state if nothing at all */}
        {!hasJordaanOrPlantage && sassysOrders.length === 0 && otherOrders.length === 0 && (
          <EmptyState tabKey="needsaction" />
        )}
      </div>
    );
  }

  function renderNeedsAction() {
    if (isPK) return renderPKNeedsAction();
    if (!needsActionOrders.length) return <EmptyState tabKey="needsaction" />;
    return Object.entries(groupBy(needsActionOrders, r => r.venue || 'Unknown')).map(([venue, reqs]) => (
      <div key={venue} style={{ marginBottom: '1.25rem' }}>
        <GroupHeading label={`To fulfil: ${shortName(venue)}`} />
        {renderCards(reqs, 'needsaction')}
      </div>
    ));
  }

  // PK en-route: grouped by destination venue, view only
  function renderPKEnRoute() {
    if (!enRouteOrders.length) return <EmptyState tabKey="enroute" />;
    return Object.entries(groupBy(enRouteOrders, r => r.venue || 'Unknown')).map(([venue, reqs]) => (
      <div key={venue} style={{ marginBottom: '1.25rem' }}>
        <GroupHeading label={`Sent to: ${shortName(venue)}`} />
        {renderCards(reqs, 'enroute')}
      </div>
    ));
  }

  function renderHistory() {
    if (!historyOrders.length) return <EmptyState tabKey="history" />;
    return Object.entries(groupBy(historyOrders, r => r.venue || 'Unknown')).map(([venue, reqs]) => (
      <div key={venue} style={{ marginBottom: '1.25rem' }}>
        <GroupHeading label={shortName(venue)} />
        {renderCards(reqs, 'history')}
      </div>
    ));
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1060px', margin: '0 auto', background: C.cream, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>
      {showLemon && <LemonBurst />}

      {/* Initials modal for generic users before sending */}
      {showSentByModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(44,58,66,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: C.white, borderRadius: '16px', padding: '1.5rem', maxWidth: '340px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.mid, marginBottom: '0.3rem' }}>Who is sending this?</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: C.charcoal, marginBottom: '1rem' }}>Enter your initials to confirm</div>
            <input
              value={sentByInitials}
              onChange={e => setSentByInitials(e.target.value.toUpperCase())}
              placeholder='e.g. EG'
              maxLength={4}
              autoFocus
              style={{ width: 90, padding: '8px 12px', textAlign: 'center', border: `2px solid ${sentByInitials.trim() ? C.navy : C.border}`, borderRadius: 8, fontSize: '1.2rem', fontWeight: 800, color: C.navy, outline: 'none', letterSpacing: '0.12em', boxSizing: 'border-box', marginBottom: '1rem', display: 'block' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => { setShowSentByModal(false); setPendingSentByReq(null); }}
                style={{ flex: 1, padding: '0.7rem', border: `1.5px solid ${C.border}`, borderRadius: 10, background: C.white, color: C.mid, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmSentByInitials} disabled={!sentByInitials.trim()}
                style={{ flex: 2, padding: '0.7rem', border: 'none', borderRadius: 10, background: sentByInitials.trim() ? C.green : C.border, color: sentByInitials.trim() ? C.white : C.mid, fontWeight: 700, fontSize: '0.9rem', cursor: sentByInitials.trim() ? 'pointer' : 'default' }}>
                <Send size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Confirm & Send
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: '0 0 0.18rem', fontSize: '1.2rem', fontWeight: 700, color: C.charcoal }}>Transfers</h2>
        <p style={{ margin: 0, fontSize: '0.8rem', color: C.mid }}>{isPK ? 'Orders to fulfil and send out' : `Internal stock movements — ${userVenue}`}</p>
      </div>

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', overflowX: 'auto', paddingBottom: '0.1rem' }}>
        {mainTabs.map(key => <SectionPill key={key} tabKey={key} isActive={activeTab === key} onClick={() => { setActiveTab(key); setShowHistory(false); }} count={counts[key]} singleTab={mainTabs.length <= 1} />)}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          onClick={() => setShowHistory(h => !h)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.28rem 0.85rem', borderRadius: '999px', border: `1.5px solid ${showHistory ? C.green : C.border}`, background: showHistory ? C.greenLight : C.white, color: showHistory ? C.green : C.mid, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
          <ClipboardList size={12} />
          History {counts.history > 0 && `(${counts.history})`}
        </button>
      </div>

      <p style={{ fontSize: '0.78rem', color: C.mid, marginBottom: '1rem', marginTop: 0 }}>
        {showHistory ? 'Received orders — last 7 days' : TAB_CONFIG[activeTab]?.description}
      </p>

      {showHistory
        ? renderHistory()
        : activeTab === 'pending'     ? renderIncoming(pendingOrders, 'pending')
        : activeTab === 'enroute'     ? (isPK ? renderPKEnRoute() : renderIncoming(enRouteOrders, 'enroute'))
        : activeTab === 'needsaction' ? renderNeedsAction()
        : null
      }
    </div>
  );
}